import moment from "moment";
import { toast } from "react-toastify";
import { getData, TOKEN } from "./Storage";

/**
 * Resets the logout flag. Call this on app initialization or after successful login.
 */
export const resetLogoutFlag = (): void => {
  (window as any).__isLoggingOut = false;
};

// Reset the flag on module load to handle page refreshes
resetLogoutFlag();

/**
 * Interface for token object stored in localStorage
 */
interface TokenData {
  token: string;
  provider?: string;
  exp?: string;
}

/**
 * Interface for decoded JWT payload
 */
interface DecodedJWT {
  exp: number | string;
  iat?: number;
  aud?: string;
  iss?: string;
  [key: string]: any;
}

/**
 * Decodes a JWT token (without verification) to extract the payload
 * @param {string} token - The JWT token string
 * @returns {DecodedJWT | null} The decoded payload or null if invalid
 */
const decodeJWT = (token: string): DecodedJWT | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

/**
 * Checks if the Core token is expired or about to expire soon.
 * 
 * @param {number} bufferMinutes - Optional buffer time in minutes (default: 2 minutes)
 * @returns {Promise<{isExpired: boolean, tokenData: TokenData | null}>} 
 *          Returns an object with isExpired flag and token data
 */
export const checkTokenExpiry = async (
  bufferMinutes: number
): Promise<{ isExpired: boolean; tokenData: TokenData | null }> => {
  try {
    const tokenData: any = await getData(TOKEN);

    // If no token data exists, consider it expired
    if (!tokenData || !tokenData.token || typeof tokenData.token !== 'string') {
      console.warn("Token data not found or invalid");
      return { isExpired: true, tokenData: null };
    }

    // Decode the JWT token to get the exp value
    const decodedToken = decodeJWT(tokenData.token);
    
    if (!decodedToken || !decodedToken.exp) {
      console.warn("Unable to decode token or exp not found. Allowing API call to proceed - API will validate.");
      return { isExpired: false, tokenData };
    }

    // The exp value in JWT - if string, keep first 7 characters then convert to number
    const tokenEpoch = typeof decodedToken.exp === 'string' 
      ? Number(decodedToken.exp.slice(0, 10)) 
      : Number(decodedToken.exp);
    
    // Validate that exp is a valid number
    if (typeof tokenEpoch !== 'number' || isNaN(tokenEpoch)) {
      console.warn("Token exp is invalid. Allowing API call to proceed - API will validate.");
      return { isExpired: false, tokenData };
    }
    
    // Get current time in seconds
    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    
    // Calculate buffer in seconds
    const bufferInSeconds = bufferMinutes * 60;
    
    // Check if token is expired or will expire within buffer time
    const isExpired = currentTimeInSeconds >= (tokenEpoch - bufferInSeconds);

    console.log('seconds until token expiry:', tokenEpoch - currentTimeInSeconds - bufferInSeconds);
    if (isExpired) {
      const expiryTime = moment.unix(tokenEpoch).format("YYYY-MM-DD HH:mm:ss");
      console.warn(`Token expired or expiring soon. Expiry time: ${expiryTime}`);
    }

    return { isExpired, tokenData };
  } catch (error) {
    console.error("Error checking token expiry:", error);
    // On error, allow call to proceed - API will validate
    return { isExpired: false, tokenData: null };
  }
};

/**
 * Handles expired token by showing a toast message and redirecting to login
 * 
 * @param {string} customMessage - Optional custom message to display
 */
export const handleExpiredToken = (
  customMessage: string = "Your session has expired. Please log in again."
): void => {
  // Prevent multiple calls
  if ((window as any).__isLoggingOut) {
    return;
  }
  (window as any).__isLoggingOut = true;

  // Use toastId to prevent duplicate toasts
  toast.error(customMessage, {
    toastId: 'token-expired',
    autoClose: 3000,
  });

  setTimeout(() => {
    localStorage.clear();
    sessionStorage.clear();
    
    // Redirect to login page instead of just reloading
    // This ensures proper redirection to Azure login if needed
    window.location.href = '/';
  }, 3000);
};

/**
 * Validates token before making API calls to critical endpoints
 * Returns true if token is valid, false otherwise and triggers logout flow
 * 
 * @param {number} bufferMinutes - Optional buffer time in minutes
 * @returns {Promise<boolean>} Returns true if token is valid, false otherwise
 */
export const validateTokenBeforeApiCall = async (
  bufferMinutes: number = 1
): Promise<boolean> => {
  const { isExpired } = await checkTokenExpiry(bufferMinutes);

  if (isExpired) {
    handleExpiredToken();
    return false;
  }

  return true;
};
